import React, { ReactElement, useContext } from 'react'

import Heading, { HeadingLevel } from '../../Common/Components/Heading'
import Fieldset from '../../Common/Components/Fieldset'
import P from '../../Common/Components/P'
import Checkbox from '../../Common/Components/Checkbox'
import FormRow from '../../Common/Components/FormRow'

import { LanguageContext } from '../../Common/Contexts'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { DeviceFiltering as DeviceFilteringLanguage } from '../../../../i18n'
import { HelpTagTitles, Tags } from '../../Common/API/Contentful/Constants'
import { HelpTag } from '../../Common/Components/HelpTag/HelpTag'
import { SelectRangeItem } from '../../Common/Components/Selects'
import { SelectOption } from '../../Common/Components/Selects/Types'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { SearchableEntities, searchEntitiesAcrossPartitions } from '../../Common/API/SearchingEntityAPIClient'
import { RangeCombo } from '../../Common/Components/Selects/RangeCombo/RangeCombo'
import { ANY_VALUE_STRING_DEFAULT } from '../../Common/Constants/DefaultValues'

/**
 * Component properties type.
 */
type Props = {
    includeCallsForGrpDevEnabled: boolean
    includeCallsForGrpDev: boolean
    setIncludeCallsForGrpDev: (checked: boolean) => void
    calcACDStatsForAgntsSignedIntoSpecGrpEnabled: boolean
    calcACDStatsForAgntsSignedIntoSpecGrp: boolean
    setCalcACDStatsForAgntsSignedIntoSpecGrp: (checked: boolean) => void
    includeOutboundCallsForSpecifiedDevicesEnabled: boolean
    setIncludeOutboundCallsForSpecifiedDevices: (checked: boolean) => void
    availableDevices: SelectRangeItem[]
    outboundCallsSpecifiedDevices: string
    setOutboundCallsSpecifiedDevices: (newValue: string) => void
    selectedPartitionsPartIDOnTelSys: Nullable<number[]>
    deviceTypes: number[]
    telSys: number
    currentTelSysType: number
}

/**
 * Device filtering section in Advanced tab of report configuration modal window.
 * @param includeCallsForGrpDevEnabled Flag specifying whether setting corresponding property is allowed.
 * @param includeCallsForGrpDev Report property.
 * @param setIncludeCallsForGrpDev Setter for corresponding report property.
 * @param calcACDStatsForAgntsSignedIntoSpecGrpEnabled Flag specifying whether setting corresponding property is allowed.
 * @param calcACDStatsForAgntsSignedIntoSpecGrp Report property.
 * @param setCalcACDStatsForAgntsSignedIntoSpecGrp Setter for corresponding report property.
 * @param includeOutboundCallsForSpecifiedDevicesEnabled Flag specifying whether setting corresponding property is allowed.
 * @param setIncludeOutboundCallsForSpecifiedDevices Setter for corresponding report property.
 * @param availableDevices List of available devices.
 * @param setAvailableDevices Setter for list of available devices.
 * @param outboundCallsSpecifiedDevices Report property.
 * @param setOutboundCallsSpecifiedDevices Setter for corresponding report property.
 * @param selectedPartitionsPartIDOnTelSys Selected partitions IDs on the current telephone system.
 * @param telSys Current telephone system ID.
 * @param deviceTypes Current device types.
 * @param currentTelSysType Current telephone system type.
 */
const DeviceFiltering: React.FC<Props> = ({
    includeCallsForGrpDevEnabled,
    includeCallsForGrpDev,
    setIncludeCallsForGrpDev,
    calcACDStatsForAgntsSignedIntoSpecGrpEnabled,
    calcACDStatsForAgntsSignedIntoSpecGrp,
    setCalcACDStatsForAgntsSignedIntoSpecGrp,
    includeOutboundCallsForSpecifiedDevicesEnabled,
    setIncludeOutboundCallsForSpecifiedDevices,
    availableDevices,
    outboundCallsSpecifiedDevices: devices,
    setOutboundCallsSpecifiedDevices: setDevices,
    selectedPartitionsPartIDOnTelSys,
    telSys,
    deviceTypes,
    currentTelSysType
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(DeviceFilteringLanguage.NAME)

    return (
        <Fieldset className={'device-filtering'}>
            <Heading level={HeadingLevel.ONE}>
                {language.get(DeviceFilteringLanguage.DeviceFiltering_SectionHeader)}
                <HelpTag
                    className='configure-report-help-title-tag'
                    helpContentTitle={HelpTagTitles.CONFIGURE_REPORT_DEVICE_FILTERING}
                    tag={Tags.CONFIGURE_REPORT}
                />
            </Heading>
            <P>{language.get(DeviceFilteringLanguage.DeviceFiltering_SectionPara)}</P>
            <FormRow isInline className={'include-calls-for-group-devices'}>
                <Checkbox
                    disabled={!includeCallsForGrpDevEnabled}
                    onChange={setIncludeCallsForGrpDev}
                    isChecked={includeCallsForGrpDev}
                    labelLeft={language.get(DeviceFilteringLanguage.IncludecallsforgrpdevmmbrsLabel)}
                />
            </FormRow>
            <FormRow isInline className={'calculate-acd-stats-for-agents'}>
                <Checkbox
                    disabled={!calcACDStatsForAgntsSignedIntoSpecGrpEnabled}
                    onChange={setCalcACDStatsForAgntsSignedIntoSpecGrp}
                    isChecked={calcACDStatsForAgntsSignedIntoSpecGrp}
                    labelLeft={language.getWithTelSysCheck(
                        DeviceFilteringLanguage.CalcacdstatsforagentssignedinspecgrpLabel,
                        currentTelSysType
                    )}
                />
            </FormRow>
            <FormRow isInline className={'include-outbound-calls-for-specified-devices'}>
                <Checkbox
                    onChange={(value: boolean): void => {
                        if (!value) setDevices(ANY_VALUE_STRING_DEFAULT)
                        return setIncludeOutboundCallsForSpecifiedDevices(value)
                    }}
                    isChecked={includeOutboundCallsForSpecifiedDevicesEnabled}
                    labelLeft={language.getWithTelSysCheck(
                        DeviceFilteringLanguage.IncludeOutboundCallsForSpecifiedDevicesLabel
                    )}
                />
                <RangeCombo
                    options={availableDevices.map(
                        (availableDevice: SelectRangeItem): SelectOption<string> => ({
                            label: availableDevice.description ?? EMPTY_STRING,
                            value: availableDevice.value,
                            renderOptionElement: availableDevice.renderElement
                        })
                    )}
                    disabled={!includeOutboundCallsForSpecifiedDevicesEnabled}
                    allItemIndex={0}
                    originalValue={devices}
                    onChange={(value?: string): void => setDevices(value ?? EMPTY_STRING)}
                    asyncSearchingFunction={
                        isVarNotEmpty(selectedPartitionsPartIDOnTelSys) && selectedPartitionsPartIDOnTelSys.length > 0
                            ? (search: string) =>
                                  searchEntitiesAcrossPartitions(
                                      SearchableEntities.DEVICES,
                                      { telSys, partitions: selectedPartitionsPartIDOnTelSys },
                                      search,
                                      deviceTypes
                                  )
                            : undefined
                    }
                    fullWidth
                />
            </FormRow>
        </Fieldset>
    )
}

export default React.memo(DeviceFiltering)
